import { FC, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Input } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import Modal from '@common/Modal'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import CheckboxViolet from '@UI/New/ChecboxViolet/CheckboxViolet'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { reportAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  SEND_FOR_SIGNATURE_MANUAL_PHONE_FORMAT_HINT,
  SEND_FOR_SIGNATURE_PARENT_HINT,
  SEND_FOR_SIGNATURE_SIGNER_PHONE_HINT,
} from '../../utils/constants'
import {
  buildSendForSignaturePayload,
  extractSendForSignatureResponse,
  getSendForSignatureFailureMessage,
  isSendForSignatureFailure,
  parseSendForSignatureError,
  RecipientMode,
} from '../../utils/sendForSignature.utils'
import cn from './index.module.css'

export type SendForSignatureContract = {
  id: number
  name?: string | null
  file_path?: string | null
}

export type SendForSignatureStudent = {
  id?: number
  name?: string | null
  phone?: string | null
  parents?: Array<{
    id: number
    name?: string | null
    phone?: string | null
  }>
}

interface ISendForSignatureModal {
  open: boolean
  contract: SendForSignatureContract | null
  student: SendForSignatureStudent | null
  onClose: () => void
  onSuccess?: (updated: Record<string, unknown>) => void
  /**
   * Кастомная отправка (например crm-lead-contract/send-for-signature).
   * Если нет — reportAPI.contractReportSendForSignature.
   */
  sendRequest?: (
    contractId: number,
    data: Record<string, unknown>,
  ) => Promise<unknown>
}

const SendForSignatureModal: FC<ISendForSignatureModal> = ({
  open,
  contract,
  student,
  onClose,
  onSuccess,
  sendRequest,
}) => {
  const addAlert = useAddAlert()
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('student')
  const [parentId, setParentId] = useState<string | null>(null)
  const [manualSignerName, setManualSignerName] = useState('')
  const [manualSignerPhone, setManualSignerPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const parents = useMemo(() => {
    const list = student?.parents
    return Array.isArray(list) ? list : []
  }, [student?.parents])

  const parentOptions = useMemo(
    () =>
      parents.map((parent) => ({
        label: parent.name || String(parent.id),
        value: String(parent.id),
      })),
    [parents],
  )

  const selectedParent = useMemo(
    () => parents.find((parent) => String(parent.id) === parentId) ?? null,
    [parentId, parents],
  )

  useEffect(() => {
    if (!open) {
      setRecipientMode('student')
      setParentId(null)
      setManualSignerName('')
      setManualSignerPhone('')
      setSubmitting(false)
      return
    }

    if (parents.length) {
      setParentId(String(parents[0].id))
    }
  }, [open, parents])

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  const handleSubmit = async () => {
    if (!contract || submitting) return

    if (recipientMode === 'parent' && !parentId) {
      return
    }

    if (recipientMode === 'manual') {
      if (!manualSignerName.trim() || !manualSignerPhone.trim()) {
        addAlert({
          title: 'Ошибка',
          text: 'Укажите ФИО и телефон подписанта',
          type: 'error',
        })
        return
      }
    }

    const payload = buildSendForSignaturePayload({
      recipientMode,
      parentId: parentId ? Number(parentId) : null,
      manualSignerName,
      manualSignerPhone,
    })

    setSubmitting(true)
    try {
      const res = sendRequest
        ? await sendRequest(contract.id, payload)
        : await reportAPI.contractReportSendForSignature({
            id: contract.id,
            data: payload,
          })

      if (isSendForSignatureFailure(res)) {
        addAlert({
          title: 'Ошибка',
          text: getSendForSignatureFailureMessage(res),
          type: 'error',
        })
        return
      }

      const updated = extractSendForSignatureResponse(
        res as { data?: Record<string, unknown> } | undefined,
      )
      if (updated) {
        onSuccess?.(updated)
      }
      addAlert({
        title: 'Готово',
        text: 'Договор отправлен на подпись',
        type: 'success',
      })
      onClose()
    } catch (error: unknown) {
      addAlert({
        title: 'Ошибка',
        text: parseSendForSignatureError(error),
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const isSubmitDisabled =
    submitting ||
    (recipientMode === 'parent' && !parentId) ||
    (recipientMode === 'manual' &&
      (!manualSignerName.trim() || !manualSignerPhone.trim()))

  const selectRecipientMode = (
    mode: RecipientMode,
    onSelect?: () => void,
  ) => {
    if (submitting) return
    setRecipientMode(mode)
    onSelect?.()
  }

  const renderRecipientCheckbox = (mode: RecipientMode, label: string) => (
    <CheckboxViolet
      value={recipientMode === mode}
      text={label}
      className={classNames(
        cn.checkboxViolet,
        submitting && cn.checkboxDisabled,
      )}
      blockClassName={
        recipientMode !== mode ? cn.checkboxBlockUnchecked : undefined
      }
      textClassName={cn.checkboxText}
      onClick={() => selectRecipientMode(mode)}
    />
  )

  return createPortal(
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={handleClose}
      style={{ zIndex: '110' }}
      styleContent={{
        minWidth: 'auto',
        padding: 0,
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'hidden',
      }}
    >
      <div className={classNames(mclasses.modalContainer, cn.modalContainer)}>
        <div className={classNames(mclasses.modalHeader, cn.modalHeader)}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Отправка документа на подписание
            </span>
          </div>
          <button
            type="button"
            className={mclasses.closeIcon}
            onClick={handleClose}
            aria-label="Закрыть"
            disabled={submitting}
          >
            <IcoRemove />
          </button>
        </div>

        <div className={classNames(mclasses.formWrapper, cn.modalBody)}>
          <p className={mclasses.label}>Название документа</p>
          <div className={cn.readonlyValue}>{contract?.name || '—'}</div>

          <div className={cn.sectionTitleRow}>
            <p className={cn.sectionTitle}>Выберите телефон подписанта</p>
            <TooltipWrapper title={SEND_FOR_SIGNATURE_SIGNER_PHONE_HINT}>
              <span className={cn.infoIconTrigger}>
                <InfoCircleOutlined
                  className={cn.infoIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </span>
            </TooltipWrapper>
          </div>

          <div className={cn.optionBlock}>
            <div className={cn.checkboxRow}>
              {renderRecipientCheckbox('student', 'Учащийся является подписантом')}
            </div>
            {recipientMode === 'student' && (
              <div className={cn.signerDetails}>
                <div className={cn.readonlyField}>
                  <span className={cn.readonlyLabel}>ФИО</span>
                  <span>{student?.name || '—'}</span>
                </div>
                <div className={cn.readonlyField}>
                  <span className={cn.readonlyLabel}>Телефон</span>
                  <span>{student?.phone || '—'}</span>
                </div>
              </div>
            )}
          </div>

          {parents.length > 0 && (
            <div className={cn.optionBlock}>
              <div className={cn.optionTitleRow}>
                <CheckboxViolet
                  value={recipientMode === 'parent'}
                  text="Подписантом является родитель"
                  className={classNames(
                    cn.checkboxViolet,
                    submitting && cn.checkboxDisabled,
                  )}
                  blockClassName={
                    recipientMode !== 'parent'
                      ? cn.checkboxBlockUnchecked
                      : undefined
                  }
                  textClassName={cn.checkboxText}
                  onClick={() =>
                    selectRecipientMode('parent', () => {
                      if (!parentId && parents[0]) {
                        setParentId(String(parents[0].id))
                      }
                    })
                  }
                />
                <TooltipWrapper title={SEND_FOR_SIGNATURE_PARENT_HINT}>
                  <span className={cn.infoIconTrigger}>
                    <InfoCircleOutlined
                      className={cn.infoIcon}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </span>
                </TooltipWrapper>
              </div>
              {recipientMode === 'parent' && (
                <div className={cn.signerDetails}>
                  {parents.length > 1 && (
                    <>
                      <p className={mclasses.label}>Родитель</p>
                      <Select
                        className={iclasses.newInputDesign}
                        value={parentId ?? undefined}
                        options={parentOptions}
                        disabled={submitting}
                        showSearch
                        onChange={(value) =>
                          setParentId(value ? String(value) : null)
                        }
                      />
                    </>
                  )}
                  <div className={cn.readonlyField}>
                    <span className={cn.readonlyLabel}>ФИО</span>
                    <span>{selectedParent?.name || '—'}</span>
                  </div>
                  <div className={cn.readonlyField}>
                    <span className={cn.readonlyLabel}>Телефон</span>
                    <span>{selectedParent?.phone || '—'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={cn.optionBlock}>
            <div className={cn.checkboxRow}>
              {renderRecipientCheckbox(
                'manual',
                'Ввести номер и ФИО подписанта',
              )}
            </div>
            {recipientMode === 'manual' && (
              <div className={cn.signerDetails}>
                <p className={mclasses.label}>ФИО подписанта</p>
                <Input
                  className={iclasses.newInputDesign}
                  value={manualSignerName}
                  disabled={submitting}
                  onChange={(event) => setManualSignerName(event.target.value)}
                />
                <div className={cn.labelRow}>
                  <p className={classNames(mclasses.label, cn.labelInline)}>
                    Телефон подписанта
                  </p>
                  <TooltipWrapper title={SEND_FOR_SIGNATURE_MANUAL_PHONE_FORMAT_HINT}>
                    <span className={cn.infoIconTrigger}>
                      <InfoCircleOutlined
                        className={cn.infoIcon}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </span>
                  </TooltipWrapper>
                </div>
                <Input
                  className={iclasses.newInputDesign}
                  value={manualSignerPhone}
                  disabled={submitting}
                  onChange={(event) => setManualSignerPhone(event.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className={classNames(mclasses.actions, cn.modalFooter)}>
          <Button color="gray" onClick={handleClose} disabled={submitting}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={() => void handleSubmit()}
            isLoading={submitting}
            disabled={isSubmitDisabled}
          >
            Отправить
          </Button>
        </div>
      </div>
    </Modal>,
    document.body,
  )
}

export default SendForSignatureModal
